import React, { useEffect } from 'react';
import Highcharts from 'highcharts/highmaps';
import HighchartsReact from 'highcharts-react-official';
import { Grid, Typography } from '@mui/material';
import {
  CountryLabel,
  GradientBox,
  LegendContainer,
  MapLabelBox,
  MapLabelGrid,
  MapLabelText,
  NoteForMap,
  StyledHighchartsMap,
} from './AppMap.Styled';
import { mapOptions, metricsGrowthInPercentage, metricsInPercentage } from './mapOptions';
import { IndiaMap } from '../../assets/images/UI-dashboard';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchTopologyData, selectTopology, setUpdatedStates } from '../../features/map/mapSlice';
import { RootState } from '../../state/store';
import { resetForm, setSelectedReplacedPack } from '../FormComponent/formSlice';
import { resetBrandValues } from '../../features/brand/brandSlice';
import { resetInputKpiState } from '../../features/inputKpi/inputKpiSlice';
import { fetchPerformanceTableDataThunk } from '../../features/performanceTable/performanceTableSlice';
import { fetchFilters } from '../../features/filters/filterSlice';
import {
  fetchSegment,
  resetStatesWithAvailablePackSegments,
  setIsPackReplacable,
} from '../../features/packSegments/packSegmentsSlice';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { allAvailableStates, groupMappings, mapReplacements } from './regionsData';
import { calculateRange, deepClone, formatNumber, updateArrayByDifference } from '../../utils/helper';

const AppMap: React.FC = () => {
  const dispatch = useAppDispatch();

  const topology = useAppSelector(selectTopology);
  const statesToShow = useAppSelector((state: RootState) => state.filters.enabledKeys.state);
  const { regionData } = useAppSelector((state: RootState) => state.map);
  const currentUpdatedStates = useAppSelector((state: RootState) => state.map.updatedStates);
  const updateSelection = (statesToShow: string[]): string[] => {
    const updatedSelection: string[] = [];

    statesToShow &&
      statesToShow.forEach((state) => {
        if (mapReplacements[state]) {
          updatedSelection.push(...mapReplacements[state]);
        } else {
          updatedSelection.push(state);
        }
      });

    return updatedSelection;
  };
  const selectedStates = updateSelection(statesToShow);
  const overallData = useAppSelector((state: RootState) => state.map.allData);
  const packSegmentsStates = useAppSelector(
    (state: RootState) => state.segment.statesWithAvailablePackSegments?.enabled_keys?.state ?? [],
  );
  const statesWithAvailablePackSegments = updateSelection(packSegmentsStates);
  const filtersState = useAppSelector((state) => state.filters);
  const { selectedMetric } = useAppSelector((state) => state.metrics);

  useEffect(() => {
    dispatch(fetchTopologyData());
    const loadMapData = async () => {};
    loadMapData();
  }, [dispatch]);

  const handlePointClick = async (point: any, currentUpdatedStates: string[], dispatch: any) => {
    const stateName = point.name;
    const mappingEntry = Object.entries(groupMappings).find(([key]) => key === stateName);

    let updatedStatesArray = [...currentUpdatedStates];

    if (mappingEntry) {
      const [_, { group, states }] = mappingEntry;

      if (currentUpdatedStates.includes(group)) {
        updatedStatesArray = updatedStatesArray.filter((state) => state !== group);
      } else {
        updatedStatesArray = updatedStatesArray.filter((state) => !states.includes(state));
        updatedStatesArray.push(group);
      }
    } else {
      if (currentUpdatedStates.includes(stateName)) {
        updatedStatesArray = updatedStatesArray.filter((state) => state !== stateName);
      } else {
        updatedStatesArray.push(stateName);
      }
    }

    await dispatch(setUpdatedStates(updatedStatesArray));

    await dispatch(showLoader());
    const filterPayload = {
      state: updatedStatesArray,
      category: filtersState.currentSelection.category,
    };
    const filtersResponse: any = await dispatch(fetchFilters(filterPayload));
    await dispatch(resetForm());
    await dispatch(resetBrandValues());
    await dispatch(resetInputKpiState());
    await dispatch(resetStatesWithAvailablePackSegments());
    await dispatch(setSelectedReplacedPack(null));
    await dispatch(setIsPackReplacable(false));
    const payloadForTable = {
      bottler: filtersResponse.payload.enabled_keys.bottler,
      state: filtersResponse.payload.enabled_keys.state,
    };
    await dispatch(fetchPerformanceTableDataThunk(payloadForTable));
    const payloadForPackSegment = {
      ...payloadForTable,
      category: filtersResponse.payload.enabled_keys.category,
    };
    await dispatch(fetchSegment(payloadForPackSegment));
    await dispatch(hideLoader());
  };

  const minValue = Math.min(...regionData.map((region) => region.value || 0));

  const maxValue = Math.max(...regionData.map((region) => region.value || 0));

  const lightGreyStates = updateArrayByDifference(selectedStates, allAvailableStates);

  const darkGreyStates = updateArrayByDifference(selectedStates, statesWithAvailablePackSegments);

  const updateRegionDataWithStates = (firstData: string[], secondData: string[]) => {
    const updatedFirstData = updateArrayByDifference(secondData, firstData);
    return regionData.map((val) => {
      return {
        ...val,
        colorValue: updatedFirstData.includes(val.name) ? 3000 : secondData.includes(val.name) ? 5000 : val.colorValue,
      };
    });
  };

  const updatedRegionData = updateRegionDataWithStates(lightGreyStates, darkGreyStates);

  const ranges = calculateRange(minValue, maxValue);

  const getNoteForMap = (metric: string) => {
    const metricNotes: { [key: string]: string } = {
      Transaction: 'Values denote Abs. Transaction and Transaction Change (YoY)',
      'Volume UC': 'Values denote Abs. Volume UC and Volume UC Change (YoY)',
      NR: 'Values denote Abs. NR and NR Change (YoY)',
      Share: 'Values denote  Share and Share Change (YoY)',
      ND: 'Values denote ND and ND Change (YoY)',
      'Comp ND': 'Values denote Comp ND and Comp ND Change (YoY)',
      '% Contri to NR India': 'Values denote % Contri to NR India and % Contri to NR India Category',
      'NR/UC': 'Values denote Abs. NR/UC and NR/UC indexed to All India',
      'CCI ND%': 'Values denote  CCI ND%  and  CCI ND% Change',
    };

    return metricNotes[metric] || `Values denote Abs. ${metric} and ${metric} Change (YoY)`;
  };

  return (
    <>
      {topology && (
        <StyledHighchartsMap>
          <Grid
            style={{
              position: 'absolute',
              right: '15%',
              top: '10%',
              zIndex: 1,
              display: 'flex',
            }}
          >
            <img src={IndiaMap} alt="india-map" />
            <CountryLabel>
              India:{' '}
              {metricsInPercentage.includes(selectedMetric[0])
                ? formatNumber(overallData['value'] * 100, 1)
                : formatNumber(overallData['value'], 1)}{' '}
              (
              {metricsGrowthInPercentage.includes(selectedMetric[0])
                ? formatNumber(overallData['value_change'], 2)
                : formatNumber(overallData['value_change'] * 100, 1) + '%'}
              )
            </CountryLabel>
          </Grid>
          <HighchartsReact
            highcharts={Highcharts}
            constructorType={'mapChart'}
            options={mapOptions(
              deepClone(topology),
              updatedRegionData,
              (point) => handlePointClick(point, currentUpdatedStates, dispatch),
              selectedMetric,
            )}
          />
          <LegendContainer>
            <Grid item style={{ textAlign: 'center', marginBottom: '3px' }}>
              <Legend color="#CCCCCC" label="Pack Segment available" />
            </Grid>
            <Grid item style={{ textAlign: 'center' }}>
              <Legend color="#E8E8E8" label="Pack Segment not available" />
            </Grid>
          </LegendContainer>
          <Grid style={{ marginLeft: '4%', position: 'absolute', bottom: '1%' }}>
            <NoteForMap>Note: {getNoteForMap(selectedMetric[0])}</NoteForMap>
            <Grid
              style={{
                display: 'flex',
                gap: '10px',
                marginTop: '5px',
              }}
            >
              <GradientBox color="linear-gradient(90deg, #ED4949 0%,#FFE9EA 100%)" />
              <GradientBox color="linear-gradient(90deg, #E2FFF7 0%, #089D72 100%)" />
            </Grid>
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '5px',
                width: '510.53px',
              }}
            >
              {ranges.map((value, index) => (
                <Typography key={index} style={{ fontSize: '10px', fontWeight: 900 }}>
                  {formatNumber(value * 100, 1)}
                </Typography>
              ))}
            </Grid>
          </Grid>
        </StyledHighchartsMap>
      )}
    </>
  );
};

const Legend = ({ color, label }: { color: string; label: string }) => (
  <MapLabelGrid>
    <MapLabelBox color={color} />
    <MapLabelText>{label}</MapLabelText>
  </MapLabelGrid>
);

export default AppMap;
